import type { ApiResource } from '../schema';

export const resource: ApiResource = {
  id: 'ai',
  slug: 'ai',
  name: 'AI & Assistant',
  description:
    'The AI Helpdesk Agent and in-app chat assistant: they suggest ticket triage, draft (but never send) ticket replies, and answer IT questions — every AI output here is a draft or suggestion a human reviews and applies, never an autonomous action.',
  icon: 'SmartToy',
  endpoints: [
    {
      slug: 'aiagent-get-settings',
      method: 'GET',
      path: '/api/aiagent/settings',
      summary: 'Get AI Helpdesk Agent settings',
      description:
        'Admin-only. Returns whether the drafts-only Helpdesk Agent is enabled, which provider it uses (claude or minimax), whether a key is present (the key itself is never echoed), and the confidence thresholds used to band a suggestion as auto-eligible, assist, or escalate. draftsOnly and autoSend are fixed guarantees the UI surfaces, not settable values — this agent never sends, resolves, closes, or modifies a ticket on its own.',
      auth: 'session',
      responseExample: `{
  "enabled": true,
  "provider": "claude",
  "hasKey": true,
  "assistantReady": true,
  "minimaxReady": false,
  "configured": true,
  "autoThreshold": 0.85,
  "assistThreshold": 0.55,
  "draftsOnly": true,
  "autoSend": false
}`,
      sourceFile: 'routes/aiagent.js, lib/aiagent.js',
    },
    {
      slug: 'aiagent-put-settings',
      method: 'PUT',
      path: '/api/aiagent/settings',
      summary: 'Update AI Helpdesk Agent settings',
      description:
        'Admin-only. Enables or disables the Helpdesk Agent, switches provider, and tunes the two confidence thresholds (autoThreshold clamped to 0.5-1, assistThreshold clamped to 0-1) that decide whether a triage suggestion is banded auto-eligible, assist, or escalate. Does not accept or change an API key here — the agent reuses the key already configured for the shared assistant (/api/assistant/settings).',
      auth: 'session',
      requestBodyExample: `{
  "enabled": true,
  "provider": "claude",
  "autoThreshold": 0.85,
  "assistThreshold": 0.55
}`,
      responseExample: `{
  "ok": true,
  "provider": "claude",
  "hasKey": true,
  "configured": true
}`,
      sourceFile: 'routes/aiagent.js',
    },
    {
      slug: 'aiagent-status',
      method: 'GET',
      path: '/api/aiagent/status',
      summary: 'Get AI Helpdesk Agent status',
      description:
        'Admin-only. Reports whether the agent is configured with a live provider key (configured: true) or running the deterministic no-network simulation (simulated: true, the default and the state used in tests), plus the intents that are eligible to reach the auto-eligible band (safeIntents) versus the ones that must always escalate to a human regardless of confidence (escalateIntents, e.g. hardware, security, ambiguous).',
      auth: 'session',
      responseExample: `{
  "configured": false,
  "simulated": true,
  "provider": "claude",
  "safeIntents": ["password_reset", "account_lockout", "printer", "wifi", "software_install", "howto", "enrollment"],
  "escalateIntents": ["hardware", "security", "ambiguous"],
  "draftsOnly": true
}`,
      sourceFile: 'routes/aiagent.js, lib/aiagent.js',
    },
    {
      slug: 'aiagent-suggest-triage',
      method: 'POST',
      path: '/api/aiagent/suggest-triage/:id?',
      summary: 'Suggest a ticket triage (advisory, not applied)',
      description:
        'Admin-only. Returns a SUGGESTED intent, category, priority, and confidence for a ticket — it is advisory metadata only and is never written back to the ticket by this endpoint. Pass an existing ticket id in the path, or omit it and send title/description in the body for an ad-hoc ticket that has not been created yet. With no provider key configured (the default), the response is a deterministic, keyword-based simulation (simulated: true, no network call); when a key is present it calls the configured provider and falls back to the same simulation on any failure. The band field (auto-eligible, assist, or escalate) tells a technician how much review the suggestion needs — even an auto-eligible suggestion still requires a human to act on it.',
      auth: 'session',
      params: [{ name: 'id', in: 'path', type: 'string', description: 'Existing ticket id. Optional — omit and supply title/description in the body to triage a draft ticket instead.' }],
      requestBodyExample: `{
  "title": "Can't log in, account seems locked",
  "description": "Tried my password three times this morning and now it says my account is locked."
}`,
      responseExample: `{
  "ticketId": "",
  "triage": {
    "simulated": true,
    "intent": "account_lockout",
    "category": "Account & Access",
    "priority": "Medium",
    "confidence": 0.88,
    "band": "auto-eligible",
    "summary": "Can't log in, account seems locked",
    "flags": { "pii": false, "security": false },
    "rationale": "Keyword match on lockout language.",
    "at": "2026-07-20T09:14:02.000Z"
  }
}`,
      sourceFile: 'routes/aiagent.js, lib/aiagent.js',
    },
    {
      slug: 'aiagent-draft-reply',
      method: 'POST',
      path: '/api/aiagent/draft-reply/:id?',
      summary: 'Draft a ticket reply (advisory, never sent)',
      description:
        'Admin-only. Generates a DRAFT reply plus its underlying triage for a ticket. The draft is text only — this endpoint never sends the reply, and never resolves, closes, or otherwise modifies the ticket; a technician must copy, edit, and apply it themselves. Like suggest-triage, it accepts either an existing ticket id in the path or an ad-hoc title/description body, and returns a deterministic simulation unless a provider key is configured. The response carries autoSend: false and an explicit advisory string as a standing reminder that this is a suggestion, not an action.',
      auth: 'session',
      params: [{ name: 'id', in: 'path', type: 'string', description: 'Existing ticket id. Optional — omit and supply title/description in the body instead.' }],
      requestBodyExample: `{
  "title": "Printer in the library won't print",
  "description": "Nothing has come out of the library printer since this morning.",
  "requesterName": "Alex Rivera"
}`,
      responseExample: `{
  "ticketId": "",
  "triage": {
    "simulated": true,
    "intent": "printer",
    "category": "Hardware & Peripherals",
    "priority": "Medium",
    "confidence": 0.78,
    "band": "assist",
    "summary": "Printer in the library won't print",
    "flags": { "pii": false, "security": false },
    "rationale": "Keyword match on printer language.",
    "at": "2026-07-20T09:15:11.000Z"
  },
  "draft": {
    "simulated": true,
    "draftReply": "Hi Alex, thanks for flagging this — I'll take a look at the library printer and follow up shortly.",
    "intent": "printer",
    "band": "assist",
    "confidence": 0.78,
    "suggestedPriority": "Medium",
    "suggestedCategory": "Hardware & Peripherals",
    "advisory": "DRAFT for human review. The agent does not send, resolve, close, or modify anything — a technician approves and applies this.",
    "autoSend": false,
    "at": "2026-07-20T09:15:11.000Z"
  }
}`,
      sourceFile: 'routes/aiagent.js, lib/aiagent.js',
    },
    {
      slug: 'aiagent-suggestions-log',
      method: 'GET',
      path: '/api/aiagent/suggestions',
      summary: 'List recent agent suggestions (advisory trail)',
      description:
        'Admin-only. Returns the most recent 100 entries (newest first) of the append-only advisory log the agent writes every time it produces a triage or draft suggestion — who requested it, which ticket, the resulting band and intent, and whether it was simulated. This is a read-only audit trail; it does not record whether or how a technician acted on a suggestion.',
      auth: 'session',
      responseExample: `[
  {
    "id": "sug_7a1c",
    "kind": "draft",
    "ticketId": "tkt_9c14",
    "band": "assist",
    "intent": "printer",
    "simulated": true,
    "by": "admin",
    "at": "2026-07-20T09:15:11.000Z"
  }
]`,
      sourceFile: 'routes/aiagent.js',
    },
    {
      slug: 'assistant-chat',
      method: 'POST',
      path: '/api/assistant/chat',
      summary: 'Chat with the in-app IT assistant',
      description:
        'Requires an authenticated user; admin-only unless the admin has turned on staffAccess. Sends a conversation (up to the last 20 messages) plus an optional preset (policy, docs, ticket, or general) and free-form context to whichever provider is configured (Claude or MiniMax) and returns the reply text. This is a chat/drafting tool for policies, documentation, and ticket-reply text — it does not take any action in NexusRMM on its own. Admins are warned not to paste real student or client PII into the conversation.',
      auth: 'session',
      requestBodyExample: `{
  "preset": "ticket",
  "context": "Requester can't connect to the guest Wi-Fi in the gym.",
  "messages": [
    { "role": "user", "content": "Draft a reply letting them know we're looking into it." }
  ]
}`,
      responseExample: `{
  "reply": "Hi there, thanks for reaching out about the guest Wi-Fi in the gym — I'm looking into it now and will follow up as soon as it's resolved.",
  "usage": { "input_tokens": 210, "output_tokens": 48 },
  "model": "claude-sonnet-4-6"
}`,
      sourceFile: 'routes/assistant.js',
    },
  ],
};
